import type { Breadcrumb, ErrorEvent } from '@sentry/react';

/**
 * Sentry PHI/PII + secret scrubbing (Cyb-011).
 *
 * Patient search/filter values used to travel in URL query strings and were
 * captured verbatim in Sentry fetch/xhr/navigation breadcrumbs and event
 * request URLs. These helpers:
 *   - strip query strings from any URL before it leaves the browser,
 *   - drop console + DOM breadcrumbs (which can capture typed PHI, e.g. a name
 *     entered into a search box),
 *   - mask sensitive `key=value` / `"key":"value"` pairs in any free text.
 *
 * Wired into Sentry.init as `beforeBreadcrumb` (scrubBreadcrumb) and
 * `beforeSend` (scrubEvent). Pure + side-effect-light so they are unit-tested.
 */

const REDACTED = '[REDACTED]';

// Sensitive keys, masked wherever they appear as key/value pairs in free text.
const SENSITIVE_KEYS = [
  'name', 'firstName', 'lastName', 'fullName', 'dateOfBirth', 'dateOfBirthYear',
  'dob', 'externalId', 'mrn', 'email', 'sex', 'gender', 'phone', 'search',
  'authorization', 'token', 'password', 'secret',
].join('|');

// The leading boundary stops `name` matching inside `username`/`firstName`, and
// `search` inside `research`. The value runs to a structural delimiter (not a
// space) so a `Bearer <token>` value is masked whole.
const SENSITIVE_TEXT_PATTERN = new RegExp(
  `(?<![a-z])(${SENSITIVE_KEYS})(["']?\\s*[:=]\\s*["']?)[^"'&,;}\\]\\n]+`,
  'gi',
);

export function scrubText(input: string): string {
  return input.replaceAll(
    SENSITIVE_TEXT_PATTERN,
    (_match, key: string, separator: string) => `${key}${separator}${REDACTED}`,
  );
}

// Drop the query string (where PHI/PII filters live) from a URL, keeping the path.
export function scrubUrl(url: string): string {
  if (!url) return url;
  const queryIndex = url.indexOf('?');
  const path = queryIndex === -1 ? url : url.slice(0, queryIndex);
  return scrubText(path);
}

function scrubBreadcrumbFields(breadcrumb: Breadcrumb): Breadcrumb {
  if (breadcrumb.data) {
    for (const key of [ 'url', 'to', 'from' ] as const) {
      const value = breadcrumb.data[key] as unknown;
      if (typeof value === 'string') {
        breadcrumb.data[key] = scrubUrl(value);
      }
    }
  }
  if (typeof breadcrumb.message === 'string') {
    breadcrumb.message = scrubText(breadcrumb.message);
  }
  return breadcrumb;
}

// beforeBreadcrumb: drop noisy/risky categories, scrub URLs on the rest.
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  if (breadcrumb.category === 'console' || breadcrumb.category?.startsWith('ui.')) {
    return null;
  }
  return scrubBreadcrumbFields(breadcrumb);
}

// beforeSend: final safety net over request URL, breadcrumbs, message + exceptions.
export function scrubEvent(event: ErrorEvent): ErrorEvent {
  if (event.request) {
    if (typeof event.request.url === 'string') {
      event.request.url = scrubUrl(event.request.url);
    }
    if (event.request.query_string != null) {
      event.request.query_string = REDACTED;
    }
  }

  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map(crumb => scrubBreadcrumbFields(crumb));
  }

  if (typeof event.message === 'string') {
    event.message = scrubText(event.message);
  }

  for (const exception of event.exception?.values ?? []) {
    if (typeof exception.value === 'string') {
      exception.value = scrubText(exception.value);
    }
  }

  return event;
}
